import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../lib/Icon';
import './../styles/landing.css';

type Lang = 'en' | 'th';

interface HeroCard {
  icon: string;
  title: string;
  titleTh: string;
  tag: string;
  tagTh: string;
  body: string;
  bodyTh: string;
  accent: string;
  border: string;
  bg: string;
}

interface Bid {
  alias: string;
  score: number;
  pct: string;
  color: string;
  border: string;
}

interface NetNodeDef {
  x: number;
  y: number;
  r: number;
  tag: string;
  label: string;
  fill: string;
}

interface NetNode extends NetNodeDef {
  ty: number;
}

interface NetEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke: string;
  dash: string;
  w: string;
  cls: string;
}

interface HashRow {
  action: string;
  hash: string;
  cls: string;
}

interface Stat {
  value: string;
  label: string;
  color: string;
}

// ---- Static data (transcribed verbatim from the source renderVals()) ----

const HERO_CARDS: HeroCard[] = [
  { icon: 'inbox', title: 'PR-2026-0187 from ERP', titleTh: 'PR-2026-0187 จาก ERP', tag: '$1,650,000', tagTh: '$1,650,000', body: 'GPS trackers for the Q3 fleet. Budget verified — COI declaration required before review.', bodyTh: 'อุปกรณ์ติดตาม GPS สำหรับฟลีตไตรมาส 3 งบประมาณตรวจสอบแล้ว — ต้องประกาศความขัดแย้งทางผลประโยชน์ก่อนการพิจารณา', accent: 'var(--color-blue-700)', border: 'var(--color-border)', bg: 'var(--color-white)' },
  { icon: 'eye-off', title: 'Three sealed bids in', titleTh: 'ข้อเสนอแบบปิดสามรายการเข้ามา', tag: 'A · B · C', tagTh: 'A · B · C', body: 'Identities masked. AI scores 94 / 87 / 79 on price, quality, delivery, performance, compliance.', bodyTh: 'ปกปิดตัวตน AI ให้คะแนน 94 / 87 / 79 ในด้านราคา คุณภาพ การส่งมอบ ประสิทธิภาพ การปฏิบัติตามข้อกำหนด', accent: 'var(--color-blue-700)', border: 'var(--color-border)', bg: 'var(--color-white)' },
  { icon: 'shield-alert', title: 'Shared director flagged', titleTh: 'ตั้งค่าสถานะกรรมการร่วมกัน', tag: '72 · High', tagTh: '72 · สูง', body: 'Two bidders share a director — registry cross-check. CCO looped in before any award.', bodyTh: 'ผู้เสนอราคาสองรายมีกรรมการร่วมกัน — ตรวจสอบข้ามทะเบียน ดึง CCO เข้ามาก่อนการมอบรางวัลใด ๆ', accent: 'var(--color-error)', border: 'var(--color-error)', bg: 'var(--color-error-bg)' },
  { icon: 'pen-tool', title: 'Walter A. signs the award', titleTh: 'Walter A. ลงนามการมอบรางวัล', tag: 'ESC-7F3A9C21', tagTh: 'ESC-7F3A9C21', body: 'DOA-routed to the right signer. Legally binding e-signature, envelope on file.', bodyTh: 'ส่งตามการมอบอำนาจไปยังผู้ลงนามที่ถูกต้อง ลายเซ็นอิเล็กทรอนิกส์ที่มีผลทางกฎหมาย จัดเก็บซองเอกสารไว้', accent: 'var(--color-primary)', border: 'var(--color-border)', bg: 'var(--color-white)' },
  { icon: 'check-circle', title: '3-Way Match passed', titleTh: 'การจับคู่ 3 ทางผ่าน', tag: '$4,620,000', tagTh: '$4,620,000', body: 'PO × goods receipt × invoice reconcile. Payment released — every step already hashed.', bodyTh: 'ใบสั่งซื้อ × ใบรับสินค้า × ใบแจ้งหนี้ กระทบยอดตรงกัน ปล่อยการจ่ายเงิน — ทุกขั้นตอนถูกแฮชไว้แล้ว', accent: 'var(--color-success)', border: 'var(--color-success)', bg: 'var(--color-success-bg)' },
];

const HERO_CLOCKS = ['09:12', '11:05', '13:40', '14:20', '16:47'];

const BIDS: Bid[] = [
  { alias: 'Supplier A', score: 94, pct: '94%', color: 'var(--color-success)', border: '2px solid var(--color-success)' },
  { alias: 'Supplier B', score: 87, pct: '87%', color: 'var(--color-warning)', border: '1px solid var(--color-border)' },
  { alias: 'Supplier C', score: 79, pct: '79%', color: 'var(--color-error)', border: '1px solid var(--color-border)' },
];

const NET_NODES: Record<string, NetNodeDef> = {
  apex: { x: 180, y: 45, r: 20, tag: 'SUP', label: 'Apex', fill: 'var(--color-blue)' },
  summit: { x: 70, y: 130, r: 20, tag: 'SUP', label: 'Summit', fill: 'var(--color-blue)' },
  pacific: { x: 90, y: 220, r: 20, tag: 'SUP', label: 'Pacific', fill: 'var(--color-blue)' },
  acme: { x: 340, y: 70, r: 20, tag: 'SUP', label: 'ACME Union', fill: 'var(--color-blue)' },
  daniel: { x: 175, y: 155, r: 14, tag: 'DIR', label: 'Daniel', fill: 'var(--color-error)' },
  victor: { x: 290, y: 170, r: 14, tag: 'DIR', label: 'Victor', fill: 'var(--color-warning)' },
};

const NET_EDGE_DEFS: [string, string, boolean][] = [
  ['daniel', 'apex', false],
  ['daniel', 'summit', false],
  ['daniel', 'pacific', true],
  ['victor', 'apex', false],
  ['victor', 'acme', false],
];

const NET_EDGES: NetEdge[] = NET_EDGE_DEFS.map(([a, b, f]) => ({
  x1: NET_NODES[a].x,
  y1: NET_NODES[a].y,
  x2: NET_NODES[b].x,
  y2: NET_NODES[b].y,
  stroke: f ? 'var(--color-error)' : 'var(--color-border-strong)',
  dash: f ? '5 4' : '0',
  w: f ? '2' : '1.5',
  cls: f ? 'lv2-edge-flag' : 'lv2-edge',
}));

const NET_NODE_LIST: NetNode[] = Object.values(NET_NODES).map((n) => ({ ...n, ty: n.y + n.r + 13 }));

const HASH_ROWS: HashRow[] = [
  { action: 'COI_DECLARE · Maria B.', hash: 'a3f9c21d8e04', cls: 'lv2-row1' },
  { action: 'BID_SUBMIT · Supplier A', hash: '2f7b9e10c8d5', cls: 'lv2-row2' },
  { action: 'FRAUD_FLAG · FF-2026-0018', hash: 'e91a4c7d3f20', cls: 'lv2-row3' },
  { action: 'DOA_APPROVE · Walter A.', hash: 'c4d80f1e2ab7', cls: 'lv2-row4' },
];

// Problem statement: today's manual gaps and what each costs the business.
const GAP_ROWS: { pain: string; painTh: string; cost: string; costTh: string }[] = [
  { pain: 'Insurance renewal is fully manual — policy by policy', painTh: 'ต่อประกันด้วยมือทั้งหมด — ทีละกรมธรรม์', cost: 'IPO / SEC audit risk — weak due-diligence trail', costTh: 'ความเสี่ยงการตรวจสอบ IPO / ก.ล.ต. — หลักฐานการสอบทานไม่แน่นหนา' },
  { pain: 'No AI supplier evaluation or matching', painTh: 'ไม่มี AI ประเมินหรือจับคู่ซัพพลายเออร์', cost: 'Fraud and collusion go undetected', costTh: 'ทุจริตและการสมรู้ร่วมคิดไม่ถูกตรวจพบ' },
  { pain: 'No automated fraud / collusion detection', painTh: 'ไม่มีระบบตรวจจับทุจริตหรือการสมรู้ร่วมคิด', cost: 'Slow manual cycles delay procurement', costTh: 'ขั้นตอนงานมือที่ล่าช้าถ่วงการจัดซื้อ' },
  { pain: 'Separation of duties not system-enforced', painTh: 'ระบบไม่บังคับการแบ่งแยกหน้าที่', cost: 'Staff time lost to manual comparison', costTh: 'เสียเวลาพนักงานไปกับการเทียบราคาด้วยมือ' },
  { pain: 'No immutable audit trail for SEC / IPO', painTh: 'ไม่มีบันทึกตรวจสอบที่เปลี่ยนแปลงไม่ได้สำหรับ ก.ล.ต. / IPO', cost: 'Overpaying vs last purchase price', costTh: 'จ่ายแพงกว่าราคาซื้อครั้งล่าสุด' },
];

// Resolution: each manual step and the tendra capability that automates it.
const AUTOMATIONS: { manual: string; manualTh: string; auto: string; autoTh: string; tag: 'RULE' | 'AI' }[] = [
  { manual: 'Pick suppliers by hand', manualTh: 'เลือกซัพพลายเออร์ด้วยตนเอง', auto: 'Auto-ranked top 5 from the ASL', autoTh: 'จัดอันดับ 5 อันดับแรกจาก ASL อัตโนมัติ', tag: 'RULE' },
  { manual: 'Compare bids in spreadsheets', manualTh: 'เทียบข้อเสนอราคาในสเปรดชีต', auto: 'AI-assisted comparison + scoring', autoTh: 'เปรียบเทียบและให้คะแนนด้วย AI', tag: 'AI' },
  { manual: 'Insurance renewal one by one', manualTh: 'ต่อประกันทีละราย', auto: 'Batch RFQ to 10–15 insurers', autoTh: 'ส่ง RFQ ชุดเดียวถึงบริษัทประกัน 10–15 ราย', tag: 'RULE' },
  { manual: 'COI checked informally', manualTh: 'ตรวจความขัดแย้งทางผลประโยชน์แบบไม่เป็นระบบ', auto: 'COI declarations + network screening', autoTh: 'ประกาศ COI + ตรวจสอบเครือข่ายอัตโนมัติ', tag: 'RULE' },
  { manual: 'No fraud detection', manualTh: 'ไม่มีการตรวจจับทุจริต', auto: 'Rule-based 5-pattern + RPT screening', autoTh: 'ตรวจ 5 รูปแบบตามกฎ + คัดกรอง RPT', tag: 'RULE' },
  { manual: 'Paper / manual audit', manualTh: 'ตรวจสอบด้วยกระดาษ', auto: 'Hash-chain immutable audit trail', autoTh: 'บันทึกตรวจสอบแบบ hash-chain เปลี่ยนแปลงไม่ได้', tag: 'RULE' },
];

const STATS: (Stat & { labelTh: string })[] = [
  { value: '6.4%', label: 'average savings vs last purchase price', labelTh: 'ประหยัดเฉลี่ยเทียบกับราคาซื้อครั้งล่าสุด', color: 'var(--color-success)' },
  { value: '100%', label: 'of awards logged in an immutable audit trail', labelTh: 'ของการมอบรางวัลถูกบันทึกในบันทึกการตรวจสอบที่เปลี่ยนแปลงไม่ได้', color: 'var(--color-fg-1)' },
  { value: '3-way', label: 'automated PO × GR × Invoice matching', labelTh: 'การจับคู่ใบสั่งซื้อ × ใบรับสินค้า × ใบแจ้งหนี้อัตโนมัติ', color: 'var(--color-fg-1)' },
  { value: '<5%', label: 'target AI-override rate, justification on file', labelTh: 'อัตราการแทนที่ AI เป้าหมาย พร้อมคำชี้แจงจัดเก็บไว้', color: 'var(--color-fg-1)' },
];

// Animate a numeric value from 0 → target on mount. Non-numeric values (e.g.
// "3-way") render as-is. Honors prefers-reduced-motion by snapping to the final
// value. Matches plain integers, decimals, and an optional trailing "%".
function CountUp({ value, duration = 1200, delay = 400 }: { value: string; duration?: number; delay?: number }) {
  const m = /^(\d+(?:\.\d+)?)(%?)$/.exec(value.trim());
  const numeric = m !== null;
  const target = numeric ? parseFloat(m![1]) : 0;
  const decimals = numeric && m![1].includes('.') ? m![1].split('.')[1].length : 0;
  const suffix = numeric ? m![2] : '';
  const [n, setN] = useState(numeric ? 0 : target);

  useEffect(() => {
    if (!numeric) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setN(target); return; }
    let raf = 0;
    let startTs = 0;
    const timer = setTimeout(() => {
      const step = (ts: number) => {
        if (!startTs) startTs = ts;
        const p = Math.min(1, (ts - startTs) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setN(target * eased);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [numeric, target, duration, delay]);

  return <>{numeric ? `${n.toFixed(decimals)}${suffix}` : value}</>;
}

export default function Landing() {
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('en');
  const [heroPhase, setHeroPhase] = useState(0);

  const toggleLang = useCallback(() => {
    setLang((l) => (l === 'th' ? 'en' : 'th'));
  }, []);

  const langLabel = lang === 'th' ? 'ไทย' : 'EN';
  const L = (en: string, th: string) => (lang === 'th' ? th : en);

  // Advance the hero source-to-pay pipeline animation every 3s.
  useEffect(() => {
    const id = setInterval(() => {
      setHeroPhase((p) => (p + 1) % 5);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Reveal-on-scroll: add lv2-in to [data-reveal] elements once ~85% into view.
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('[data-reveal]');
    if (els.length === 0) return;
    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('lv2-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lv2-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const card = HERO_CARDS[heroPhase];
  const heroClock = HERO_CLOCKS[heroPhase];
  const heroLiveColor = heroPhase === 4 ? 'var(--color-success)' : heroPhase === 2 ? 'var(--color-error)' : 'var(--color-warning)';

  return (
    <div style={{ background: 'var(--color-white)' }}>
      {/* HEADER */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="lv2-wrap lv2-nav" style={{ height: 72, display: 'flex', alignItems: 'center', gap: 28 }}>
          <a href="#top" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/tendra-logo.svg" alt="tendra — AI Sourcing" className="lv2-nav-logo" style={{ height: 52, width: 'auto' }} />
          </a>
          <div style={{ flex: 1 }} />
          <button onClick={toggleLang} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 11px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, color: 'var(--color-fg-2)' }}>
            <Icon name="globe" />
            {langLabel}
          </button>
          <a onClick={() => navigate('/login')} className="lv2-nav-signin" style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-fg-1)', cursor: 'pointer' }}>{L('Sign In', 'เข้าสู่ระบบ')}</a>
          <a onClick={() => navigate('/login')} className="lv2-btn" style={{ padding: '9px 16px', fontSize: 14, cursor: 'pointer' }}>{L('Launch App', 'เปิดแอป')} <Icon name="arrow-right" /></a>
        </div>
      </header>

      {/* HERO — centered composition with flanking floating cards + stats bar */}
      <section id="top" style={{ padding: '92px 0 72px', background: 'var(--color-mist)', overflow: 'hidden' }}>
        <div className="lv2-wrap lv2-hero-stage" style={{ position: 'relative', maxWidth: 1120 }}>

          {/* Floating card — left: the live source-to-pay beat (cycles every 3s) */}
          <div className="lv2-hero-line lv2-float lv2-float-l" style={{ animationDelay: '.55s' }} aria-hidden="true">
            <div className="lv2-panel" style={{ padding: '13px 15px', width: 244 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, letterSpacing: '.04em', color: 'var(--color-fg-3)', textTransform: 'uppercase' }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: heroLiveColor, animation: 'lv2-pulse 2s infinite' }} />
                  {L('One purchase, one day', 'หนึ่งการจัดซื้อ หนึ่งวัน')}
                </div>
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-fg-3)' }}>{heroClock}</span>
              </div>
              <div key={`ph${heroPhase}`} style={{ border: `1px solid ${card.border}`, borderRadius: 'var(--radius-sm)', padding: '11px 12px', background: card.bg, animation: 'lv2-up .45s var(--ease-out) both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Icon name={card.icon} style={{ fontSize: 16, color: card.accent }} />
                  <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>{L(card.title, card.titleTh)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--color-fg-3)' }}>{L('step', 'ขั้นตอน')} {heroPhase + 1} / 5</span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, color: card.accent }}>{L(card.tag, card.tagTh)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Floating card — right: blind AI score */}
          <div className="lv2-hero-line lv2-float lv2-float-r" style={{ animationDelay: '.7s' }} aria-hidden="true">
            <div className="lv2-panel" style={{ padding: '15px 16px', width: 210 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.04em', color: 'var(--color-fg-3)', textTransform: 'uppercase', marginBottom: 10 }}>
                <Icon name="eye-off" style={{ fontSize: 13 }} />
                {L('Blind AI score', 'คะแนน AI แบบปิด')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 34, fontWeight: 700, fontFamily: 'var(--font-mono)', letterSpacing: '-.02em', color: 'var(--color-fg-1)' }}><CountUp value="94" delay={800} /></span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-success)' }}>{L('Supplier A', 'ผู้ขาย A')}</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--color-ink-50)', overflow: 'hidden', margin: '10px 0 8px' }}>
                <div style={{ height: '100%', width: '94%', background: 'var(--color-success)', borderRadius: 999 }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-fg-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name="sparkles" style={{ fontSize: 12, color: 'var(--color-success)' }} />
                {L('AI recommends', 'AI แนะนำ')}
              </div>
            </div>
          </div>

          {/* Centered hero copy */}
          <div className="lv2-hero-copy" style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 2 }}>
            <div className="lv2-hero-line egs-eyebrow" style={{ animationDelay: '.05s', marginBottom: 20, justifyContent: 'center' }}>{L('The source-to-pay story', 'เรื่องราวจากจัดซื้อถึงจ่ายเงิน')}</div>
            <h1 className="lv2-hero-line" style={{ animationDelay: '.15s', fontSize: 58, lineHeight: 1.2, letterSpacing: '-.02em', margin: '0 0 22px' }}>
              {L('Every dollar you spend has a story. Make it one ', 'ทุกดอลลาร์ที่คุณใช้จ่ายมีเรื่องราว ทำให้เป็นเรื่องที่คุณ')}
              <span className="lv2-hl">{L('you can prove', 'พิสูจน์ได้')}</span>.
            </h1>
            <p className="lv2-hero-line" style={{ animationDelay: '.3s', fontSize: 18, color: 'var(--color-fg-2)', lineHeight: 1.55, maxWidth: 560, margin: '0 auto 30px' }}>
              {L('tendra follows one purchase from requisition to reconciled payment — blind bidding, AI scoring, and a hash-verified audit trail at every step. Scroll through a single working day.', 'tendra ติดตามการจัดซื้อหนึ่งรายการตั้งแต่ใบขอซื้อจนถึงการจ่ายเงินที่กระทบยอดแล้ว — การเสนอราคาแบบปิด การให้คะแนนด้วย AI และบันทึกการตรวจสอบที่ตรวจสอบด้วยแฮชในทุกขั้นตอน เลื่อนดูเรื่องราวตลอดหนึ่งวันทำงาน')}
            </p>
            <div className="lv2-hero-line" style={{ animationDelay: '.45s', display: 'flex', gap: 12, justifyContent: 'center' }}>
              <a onClick={() => navigate('/login')} className="lv2-btn" style={{ cursor: 'pointer' }}>{L('Launch App', 'เปิดแอป')} <Icon name="arrow-right" /></a>
              <a href="#c1" className="lv2-ghost">{L('Follow the story', 'ติดตามเรื่องราว')} <Icon name="arrow-down" /></a>
            </div>
          </div>

          {/* Stats bar — trust lead cell + headline metrics */}
          <div className="lv2-hero-line lv2-statbar" style={{ animationDelay: '.6s' }}>
            <div className="lv2-statbar-lead">
              <span className="lv2-statbar-badge"><Icon name="shield-check" style={{ color: 'var(--color-success)' }} /></span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-fg-1)' }}>{L('Trusted by procurement teams', 'ทีมจัดซื้อไว้วางใจ')}</div>
                <div style={{ fontSize: 12, color: 'var(--color-fg-3)', lineHeight: 1.35, marginTop: 2 }}>
                  {L('managing ', 'ที่บริหาร ')}<b style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-fg-2)' }}>$48M+</b>{L(' in annual spend.', ' ในยอดใช้จ่ายต่อปี')}
                </div>
              </div>
            </div>
            <div className="lv2-statbar-metric">
              <div className="lv2-statbar-num" style={{ color: 'var(--color-success)' }}><CountUp value="6.4%" delay={650} /></div>
              <div className="lv2-statbar-cap">{L('avg savings vs last price', 'ประหยัดเฉลี่ยเทียบราคาเดิม')}</div>
            </div>
            <div className="lv2-statbar-metric">
              <div className="lv2-statbar-num"><CountUp value="100%" delay={650} /></div>
              <div className="lv2-statbar-cap">{L('logged, immutable audit trail', 'บันทึกตรวจสอบที่เปลี่ยนไม่ได้')}</div>
            </div>
            <div className="lv2-statbar-metric">
              <div className="lv2-statbar-num"><CountUp value="3-way" delay={650} /></div>
              <div className="lv2-statbar-cap">{L('automated PO × GR × invoice', 'จับคู่ใบสั่งซื้อ × รับของ × ใบแจ้งหนี้')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROLOGUE — the gaps and their cost */}
      <section id="prologue" className="lv2-chapter">
        <div className="lv2-wrap">
          <div className="lv2-reveal" data-reveal style={{ maxWidth: 640, margin: '0 auto 48px', textAlign: 'center' }}>
            <div className="lv2-ts">{L('PROLOGUE · BEFORE TENDRA', 'บทนำ · ก่อน tendra')}</div>
            <h2 className="lv2-h2">{L("Today's gaps, and their cost.", 'ช่องว่างวันนี้และผลกระทบ')}</h2>
            <p style={{ fontSize: 17, color: 'var(--color-fg-2)', lineHeight: 1.55 }}>{L('Manual, un-governed procurement — and what it costs the business every year.', 'จัดซื้อด้วยมือ ขาดการควบคุม — และต้นทุนที่ธุรกิจต้องจ่ายทุกปี')}</p>
          </div>

          <div className="lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div className="lv2-reveal lv2-panel" data-reveal style={{ overflow: 'hidden' }}>
              <div style={{ padding: '13px 20px', background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)', fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: 'var(--color-fg-3)' }}>{L("CAN'T DO TODAY", 'ทำไม่ได้วันนี้')}</div>
              {GAP_ROWS.map((g, i) => (
                <div key={g.pain} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 20px', borderBottom: i < GAP_ROWS.length - 1 ? '1px solid var(--color-border)' : 'none', fontSize: 14, lineHeight: 1.45 }}>
                  <Icon name="x" style={{ color: 'var(--color-fg-3)', fontSize: 14, flex: 'none', marginTop: 3 }} />
                  {L(g.pain, g.painTh)}
                </div>
              ))}
            </div>
            <div className="lv2-reveal lv2-panel" data-reveal style={{ overflow: 'hidden' }}>
              <div style={{ padding: '13px 20px', background: 'var(--color-error-bg)', borderBottom: '1px solid var(--color-border)', fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: 'var(--color-error)' }}>{L('COST TO BUSINESS', 'ผลกระทบต่อธุรกิจ')}</div>
              {GAP_ROWS.map((g, i) => (
                <div key={g.cost} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 20px', borderBottom: i < GAP_ROWS.length - 1 ? '1px solid var(--color-border)' : 'none', fontSize: 14, lineHeight: 1.45 }}>
                  <Icon name="trending-down" style={{ color: 'var(--color-error)', fontSize: 14, flex: 'none', marginTop: 3 }} />
                  {L(g.cost, g.costTh)}
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CHAPTER 1 */}
      <section id="c1" className="lv2-chapter">
        <div className="lv2-wrap lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-ts">09:12 · {L('CHAPTER 1', 'บทที่ 1')}</div>
            <h2 className="lv2-h2">{L('A requisition arrives.', 'ใบขอซื้อเข้ามา')}</h2>
            <p className="lv2-body">
              {L('Operations needs GPS trackers for the Q3 fleet upgrade — $1.65M. The PR lands in Maria\'s queue from the ERP, budget already verified, procurement method auto-set by the threshold matrix. Before she can even open it, tendra asks one question:', 'ฝ่ายปฏิบัติการต้องการอุปกรณ์ติดตาม GPS สำหรับการอัปเกรดฟลีตไตรมาส 3 — $1.65M ใบขอซื้อเข้าสู่คิวของ Maria จาก ERP งบประมาณตรวจสอบแล้ว วิธีจัดซื้อถูกตั้งค่าอัตโนมัติตามเมทริกซ์เกณฑ์ ก่อนที่เธอจะเปิดดูด้วยซ้ำ tendra ถามคำถามเดียว:')} <b style={{ color: 'var(--color-fg-1)' }}>{L('any conflict of interest?', 'มีความขัดแย้งทางผลประโยชน์หรือไม่?')}</b>
            </p>
          </div>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-panel" style={{ padding: 22, maxWidth: 420 }}>
              <div className="lv2-row1" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--color-blue-100)', color: 'var(--color-blue-700)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="inbox" />
                </span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{L('New purchase requisition', 'ใบขอซื้อใหม่')}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-fg-3)', fontFamily: 'var(--font-mono)' }}>PR-2026-0187 · {L('from ERP', 'จาก ERP')}</div>
                </div>
              </div>
              <div className="lv2-row2" style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
                <span style={{ color: 'var(--color-fg-3)' }}>{L('GPS Tracking Devices — Fleet Q3', 'อุปกรณ์ติดตาม GPS — ฟลีตไตรมาส 3')}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>$1,650,000</span>
              </div>
              <div className="lv2-row3" style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
                <span style={{ color: 'var(--color-fg-3)' }}>{L('Budget', 'งบประมาณ')}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: 'var(--color-success-bg)', color: 'var(--color-success)' }}>{L('Verified', 'ตรวจสอบแล้ว')}</span>
              </div>
              <div className="lv2-row4" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, padding: '11px 12px', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--color-fg-2)' }}>
                <Icon name="alert-triangle" style={{ color: 'var(--color-warning)' }} />
                {L('COI declaration required before review — Code of Conduct §6.2', 'ต้องประกาศความขัดแย้งทางผลประโยชน์ก่อนการพิจารณา — จรรยาบรรณ §6.2')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 2 */}
      <section className="lv2-chapter" style={{ background: 'var(--color-mist)' }}>
        <div className="lv2-wrap lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-blue-700)', background: 'var(--color-blue-100)', borderRadius: 'var(--radius-sm)', padding: '8px 11px', marginBottom: 14 }}>
                <Icon name="eye-off" />
                {L('Blind evaluation · identities hidden until award', 'การประเมินแบบปิด · ปกปิดตัวตนจนกว่าจะมอบรางวัล')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                {BIDS.map((b) => (
                  <div key={b.alias} style={{ background: 'var(--color-white)', border: b.border, borderRadius: 'var(--radius-sm)', padding: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
                      <Icon name="eye-off" style={{ color: 'var(--color-fg-3)', fontSize: 13 }} />
                      {b.alias}
                    </div>
                    <div style={{ height: 6, borderRadius: 999, background: 'var(--color-ink-50)', overflow: 'hidden', marginBottom: 8 }}>
                      <div className="lv2-bar" style={{ height: '100%', width: b.pct, background: b.color }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{b.score}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-fg-3)' }}>{L('AI Score', 'คะแนน AI')}</div>
                  </div>
                ))}
              </div>
              <div className="lv2-pop" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 14, padding: '7px 12px', borderRadius: 999, background: 'var(--color-success-bg)', color: 'var(--color-success)', fontSize: 12, fontWeight: 700 }}>
                <Icon name="sparkles" />
                {L('AI recommends Supplier A', 'AI แนะนำ Supplier A')}
              </div>
            </div>
          </div>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-ts">11:05 · {L('CHAPTER 2', 'บทที่ 2')}</div>
            <h2 className="lv2-h2">{L('Nobody knows who\'s bidding. That\'s the point.', 'ไม่มีใครรู้ว่าใครเสนอราคา นั่นแหละคือประเด็น')}</h2>
            <p className="lv2-body">
              {L('Three sealed bids arrive through the supplier portal. To Maria they\'re just', 'ข้อเสนอแบบปิดสามรายการเข้ามาผ่านพอร์ทัลซัพพลายเออร์ สำหรับ Maria แล้วพวกเขาเป็นเพียง')} <b style={{ color: 'var(--color-fg-1)' }}>{L('Supplier A, B, and C', 'Supplier A, B และ C')}</b> {L('— no logos, no relationships, no favorites. The AI scores each one on price, quality, delivery, performance, and compliance, with weights her admin controls. Overriding the recommendation? That takes a 50-character justification and an extra approval.', '— ไม่มีโลโก้ ไม่มีความสัมพันธ์ ไม่มีการเลือกที่รัก AI ให้คะแนนแต่ละรายในด้านราคา คุณภาพ การส่งมอบ ประสิทธิภาพ และการปฏิบัติตามข้อกำหนด โดยมีน้ำหนักที่ผู้ดูแลระบบของเธอควบคุม ต้องการแทนที่คำแนะนำใช่ไหม? นั่นต้องใช้คำชี้แจง 50 ตัวอักษรและการอนุมัติเพิ่มเติม')}
            </p>
          </div>
        </div>
      </section>

      {/* CHAPTER 3 */}
      <section className="lv2-chapter">
        <div className="lv2-wrap lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-ts">13:40 · {L('CHAPTER 3', 'บทที่ 3')}</div>
            <h2 className="lv2-h2">{L('The machine sees what people miss.', 'เครื่องจักรมองเห็นสิ่งที่คนมองข้าม')}</h2>
            <p className="lv2-body">
              {L('Two of the bidders quietly share a director. No human would catch it in a filing cabinet — tendra cross-references the business registry and flags the link before the award,', 'ผู้เสนอราคาสองรายมีกรรมการร่วมกันอย่างเงียบ ๆ ไม่มีมนุษย์คนใดจับได้จากตู้เก็บเอกสาร — tendra ตรวจสอบข้ามกับทะเบียนธุรกิจและตั้งค่าสถานะความเชื่อมโยงก่อนการมอบรางวัล')} <b style={{ color: 'var(--color-fg-1)' }}>{L('collusion score 72', 'คะแนนสมรู้ร่วมคิด 72')}</b>{L('. The CCO is looped in automatically. Related-party deals and split orders get the same treatment.', ' CCO ถูกดึงเข้ามาโดยอัตโนมัติ ดีลกับผู้เกี่ยวข้องและการแบ่งคำสั่งซื้อก็ได้รับการปฏิบัติเช่นเดียวกัน')}
            </p>
          </div>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{L('Supplier network', 'เครือข่ายซัพพลายเออร์')}</span>
                <span className="lv2-pop" style={{ fontSize: 11, color: 'var(--color-error)', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700 }}>
                  <Icon name="alert-triangle" />
                  72 · {L('High', 'สูง')}
                </span>
              </div>
              <svg viewBox="0 0 420 260" style={{ width: '100%', height: 'auto', display: 'block' }}>
                {NET_EDGES.map((e, i) => (
                  <line key={i} className={e.cls} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} pathLength={200} stroke={e.stroke} strokeWidth={e.w} strokeDasharray={e.dash} />
                ))}
                {NET_NODE_LIST.map((n) => (
                  <Fragment key={n.label}>
                    <circle cx={n.x} cy={n.y} r={n.r} fill={n.fill} />
                    <text x={n.x} y={n.y} textAnchor="middle" dy="3.5" fill="#fff" fontSize={9} fontWeight={700} fontFamily="var(--font-mono)">{n.tag}</text>
                    <text x={n.x} y={n.ty} textAnchor="middle" fill="var(--color-fg-2)" fontSize={11} fontWeight={600}>{n.label}</text>
                  </Fragment>
                ))}
              </svg>
              <div style={{ fontSize: 11, color: 'var(--color-fg-3)', paddingTop: 6 }}>{L('Shared director detected across two invited bidders — flagged to CCO before award.', 'ตรวจพบกรรมการร่วมกันระหว่างผู้เสนอราคาที่ได้รับเชิญสองราย — แจ้ง CCO ก่อนการมอบรางวัล')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 4 */}
      <section className="lv2-chapter" style={{ background: 'var(--color-bg-inverse)' }}>
        <div className="lv2-wrap lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-panel" style={{ padding: 20, background: 'rgba(255,255,255,.04)', borderColor: 'rgba(255,255,255,.12)', boxShadow: 'none' }}>
              <div style={{ border: '1.5px dashed rgba(255,255,255,.3)', borderRadius: 'var(--radius-sm)', padding: 16, textAlign: 'center', marginBottom: 14 }}>
                <span style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 30, color: '#fff' }}>Walter Adams</span>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 4 }}>{L('Signed via e-Sign Cloud · Envelope', 'ลงนามผ่าน e-Sign Cloud · ซองเอกสาร')} ESC-7F3A9C21</div>
              </div>
              {HASH_ROWS.map((h) => (
                <div key={h.action} className={h.cls} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 12 }}>
                  <Icon name="check" style={{ color: 'var(--color-success)' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'rgba(255,255,255,.85)' }}>{h.action}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'rgba(255,255,255,.35)' }}>{h.hash}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-ts" style={{ color: 'rgba(255,255,255,.5)' }}>14:20 · {L('CHAPTER 4', 'บทที่ 4')}</div>
            <h2 className="lv2-h2" style={{ color: '#fff' }}>{L('Sign it. Prove it. Forever.', 'ลงนาม พิสูจน์ ตลอดกาล')}</h2>
            <p className="lv2-body" style={{ color: 'rgba(255,255,255,.75)' }}>
              {L('The award routes itself up the Delegation-of-Authority matrix to the right signer. Walter signs once, electronically, legally. And every step of the day — the COI answer, the sealed bids, the flag, the signature — is already hashed into an', 'การมอบรางวัลถูกส่งขึ้นตามเมทริกซ์การมอบอำนาจไปยังผู้ลงนามที่ถูกต้อง Walter ลงนามครั้งเดียว ทางอิเล็กทรอนิกส์ และมีผลทางกฎหมาย และทุกขั้นตอนของวันนี้ — คำตอบเรื่องความขัดแย้งทางผลประโยชน์ ข้อเสนอแบบปิด การตั้งค่าสถานะ ลายเซ็น — ถูกแฮชไว้แล้วใน')} <b style={{ color: '#fff' }}>{L('immutable audit trail', 'บันทึกการตรวจสอบที่เปลี่ยนแปลงไม่ได้')}</b> {L('your auditors can verify line by line.', 'ที่ผู้ตรวจสอบของคุณสามารถตรวจสอบได้ทีละบรรทัด')}
            </p>
          </div>
        </div>
      </section>

      {/* CHAPTER 5 */}
      <section className="lv2-chapter">
        <div className="lv2-wrap lv2-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-ts">16:47 · {L('CHAPTER 5', 'บทที่ 5')}</div>
            <h2 className="lv2-h2">{L('Payment reconciles itself.', 'การจ่ายเงินกระทบยอดด้วยตัวเอง')}</h2>
            <p className="lv2-body">
              {L('Weeks later the goods arrive, the invoice lands, and tendra runs the', 'หลายสัปดาห์ต่อมาสินค้ามาถึง ใบแจ้งหนี้เข้ามา และ tendra รัน')} <b style={{ color: 'var(--color-fg-1)' }}>{L('3-way match', 'การจับคู่ 3 ทาง')}</b> {L('— PO × goods receipt × invoice — without anyone chasing a spreadsheet. Amounts agree, payment releases. A $30,000 variance? Payment holds and compliance hears about it first.', '— ใบสั่งซื้อ × ใบรับสินค้า × ใบแจ้งหนี้ — โดยไม่ต้องมีใครไล่ตามสเปรดชีต จำนวนเงินตรงกัน การจ่ายเงินปล่อยออก มีส่วนต่าง $30,000 ใช่ไหม? การจ่ายเงินถูกระงับและฝ่ายกำกับดูแลจะได้รับแจ้งก่อนเป็นคนแรก')}
            </p>
          </div>
          <div className="lv2-reveal" data-reveal>
            <div className="lv2-panel" style={{ padding: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 18 }}>
                <div className="lv2-row1" style={{ flex: 1, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 12, textAlign: 'center' }}>
                  <Icon name="shopping-cart" style={{ color: 'var(--color-fg-3)', fontSize: 18 }} />
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>PO</div>
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-fg-3)' }}>$4,620,000</div>
                </div>
                <Icon name="x" className="lv2-row2" style={{ color: 'var(--color-fg-3)', fontSize: 14 }} />
                <div className="lv2-row2" style={{ flex: 1, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 12, textAlign: 'center' }}>
                  <Icon name="package-check" style={{ color: 'var(--color-fg-3)', fontSize: 18 }} />
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>{L('Goods Receipt', 'ใบรับสินค้า')}</div>
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-fg-3)' }}>GR-2026-0031</div>
                </div>
                <Icon name="x" className="lv2-row3" style={{ color: 'var(--color-fg-3)', fontSize: 14 }} />
                <div className="lv2-row3" style={{ flex: 1, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 12, textAlign: 'center' }}>
                  <Icon name="receipt" style={{ color: 'var(--color-fg-3)', fontSize: 18 }} />
                  <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>{L('Invoice', 'ใบแจ้งหนี้')}</div>
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--color-fg-3)' }}>$4,620,000</div>
                </div>
              </div>
              <div className="lv2-pop" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--color-success-bg)', border: '1px solid var(--color-success)', borderRadius: 'var(--radius-sm)' }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: 'var(--color-success)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="check" />
                </span>
                <div style={{ fontSize: 13, color: 'var(--color-fg-1)' }}><b>{L('3-Way Match passed.', 'การจับคู่ 3 ทางผ่าน')}</b> {L('Payment can be released.', 'สามารถปล่อยการจ่ายเงินได้')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESOLUTION — what tendra automates */}
      <section className="lv2-chapter">
        <div className="lv2-wrap" style={{ maxWidth: 980 }}>
          <div className="lv2-reveal" data-reveal style={{ maxWidth: 640, margin: '0 auto 44px', textAlign: 'center' }}>
            <div className="lv2-ts">17:15 · {L('RESOLUTION', 'บทคลี่คลาย')}</div>
            <h2 className="lv2-h2">{L('Everything manual, now automated.', 'จากงานมือสู่ระบบอัตโนมัติ')}</h2>
            <p style={{ fontSize: 17, color: 'var(--color-fg-2)', lineHeight: 1.55 }}>{L('AI where it adds value; rule-based, explainable logic everywhere else.', 'ใช้ AI เฉพาะจุดที่เพิ่มคุณค่า ที่เหลือใช้กฎที่อธิบายและตรวจสอบได้')}</p>
          </div>
          <div className="lv2-reveal lv2-panel" data-reveal style={{ overflow: 'hidden' }}>
            <div className="lv2-mapline" style={{ padding: '13px 24px', background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)', fontSize: 12, fontWeight: 700, letterSpacing: '.08em' }}>
              <span style={{ color: 'var(--color-fg-3)' }}>{L('MANUAL TODAY', 'งานมือวันนี้')}</span>
              <span className="lv2-maparrow" />
              <span style={{ color: 'var(--color-primary)' }}>{L('TENDRA AUTOMATES', 'tendra ทำให้อัตโนมัติ')}</span>
            </div>
            {AUTOMATIONS.map((a, i) => (
              <div key={a.manual} className="lv2-mapline" style={{ padding: '16px 24px', borderBottom: i < AUTOMATIONS.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                <span style={{ fontSize: 14, color: 'var(--color-fg-3)', lineHeight: 1.45 }}>{L(a.manual, a.manualTh)}</span>
                <Icon name="arrow-right" className="lv2-maparrow" style={{ color: 'var(--color-primary)', fontSize: 15 }} />
                <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.45, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {L(a.auto, a.autoTh)}
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', padding: '2px 8px', borderRadius: 999, background: a.tag === 'AI' ? 'var(--color-blue-100)' : 'var(--color-mist)', color: a.tag === 'AI' ? 'var(--color-blue-700)' : 'var(--color-fg-3)', border: a.tag === 'AI' ? 'none' : '1px solid var(--color-border)' }}>{a.tag}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EPILOGUE / STATS + CTA */}
      <section className="lv2-chapter" style={{ background: 'var(--color-mist)', textAlign: 'center' }}>
        <div className="lv2-wrap">
          <div className="lv2-reveal" data-reveal style={{ maxWidth: 640, margin: '0 auto 48px' }}>
            <div className="lv2-ts">17:40 · {L('EPILOGUE', 'บทส่งท้าย')}</div>
            <h2 className="lv2-h2" style={{ fontSize: 40 }}>{L('One purchase, fully told.', 'หนึ่งการจัดซื้อ เล่าครบถ้วน')}<br />{L('Now imagine all of them.', 'ทีนี้ลองจินตนาการถึงทั้งหมด')}</h2>
            <p style={{ fontSize: 17, color: 'var(--color-fg-2)', lineHeight: 1.55 }}>{L('Fair by design, scored in the open, provable forever — across every requisition, every supplier, every dollar.', 'ยุติธรรมโดยการออกแบบ ให้คะแนนอย่างเปิดเผย พิสูจน์ได้ตลอดกาล — ในทุกใบขอซื้อ ทุกซัพพลายเออร์ ทุกดอลลาร์')}</p>
          </div>
          <div className="lv2-reveal" data-reveal style={{ display: 'flex', flexWrap: 'wrap', gap: 24, maxWidth: 900, margin: '0 auto 48px' }}>
            {STATS.map((s) => (
              <div key={s.label} style={{ flex: '1 1 180px', background: 'var(--color-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 22 }}>
                <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em', color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'var(--color-fg-2)', marginTop: 6, lineHeight: 1.4 }}>{L(s.label, s.labelTh)}</div>
              </div>
            ))}
          </div>
          <div className="lv2-reveal" data-reveal>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 14 }}>
              <a onClick={() => navigate('/login')} className="lv2-btn" style={{ cursor: 'pointer' }}>{L('Launch App', 'เปิดแอป')} <Icon name="arrow-right" /></a>
              <a onClick={() => navigate('/register')} className="lv2-ghost" style={{ cursor: 'pointer' }}>{L('Register as a Supplier', 'ลงทะเบียนเป็นซัพพลายเออร์')}</a>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-fg-3)' }}>
              {L('English & Thai · Role-based access · MFA-ready · ', 'ไทยและอังกฤษ · การเข้าถึงตามบทบาท · รองรับ MFA · ')}<a onClick={() => navigate('/report')} style={{ fontWeight: 600, cursor: 'pointer' }}>{L('Report a concern anonymously', 'แจ้งข้อกังวลแบบไม่ระบุตัวตน')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '32px 0', borderTop: '1px solid var(--color-border)' }}>
        <div className="lv2-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <img src="/tendra-logo.svg" alt="tendra" style={{ height: 40, width: 'auto' }} />
          <div style={{ fontSize: 12, color: 'var(--color-fg-3)' }}>© 2026 ACME Auto Parts Co., Ltd. · {L('All access is logged and monitored.', 'การเข้าถึงทั้งหมดถูกบันทึกและตรวจสอบ')}</div>
          <button onClick={toggleLang} style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-fg-2)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name="globe" />
            {langLabel}
          </button>
        </div>
      </footer>
    </div>
  );
}
